import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  map,
  mergeMap,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import {
  BusinessUnitDAEngineerClient,
  BusinessUnitViewModel,
  DADeliverableTemplateDAEngineerClient,
  DADeliverableTemplateViewModel,
  ExceptionsClient,
  FocusAreaConfigViewModel,
  GlobalAdminsClient,
  GlobalAdminViewModel,
  GlobalSettingsDAEngineerClient,
  GlobalSettingsValueRecognitionViewModel,
  MandatoryCheckTemplateDAEngineerClient,
  MandatoryCheckTemplateViewModel,
  PeerPoolExceptionViewModel,
  ProjectPhaseTemplateDAEngineerClient,
  RiskAssessmentConfigDAEngineerClient,
  SMEDisciplineConfigViewModel,
  SMEDisciplineDAEngineerClient,
} from 'src/app/shared/models/autogenerated';
import {
  finishLoading,
  startLoading,
} from 'src/app/shared/store/shared.operators';
import { ToastMessagesService } from 'src/app/shared/services/toast-messages.service';
import {
  deleteMandatoryCheckTemplate,
  loadAllMandatoryChecksTemplates,
  loadGlobalSettingsValueRecognition,
  loadMandatoryCheckTemplate,
  setAllMandatoryChecksTemplates,
  setGlobalSettingsValueRecognition,
  setMandatoryCheckTemplateForm,
  updateMandatoryCheckTemplateForm,
  updateGlobalSettingsValueRecognition,
  updateMandatoryCheckTemplateFormSuccess,
  createMandatoryCheckTemplateFromFormSuccess,
  createMandatoryCheckTemplateFromForm,
  loadAllPhases,
  setAllPhases,
  syncAllGlobalAdminsAccess,
  syncAllGlobalAdminsAccessSuccess,
  loadAllPeerPoolDisciplines,
  setAllPeerPoolDisciplines,
  loadPeerPoolDiscipline,
  setPeerPoolDisciplineForm,
  createPeerPoolDiscipline,
  createPeerPoolDisciplineSuccess,
  deletePeerPoolDiscipline,
  updatePeerPoolDiscipline,
  updatePeerPoolDisciplineSuccess,
  importMandatoryChecks,
  importMandatoryChecksSuccess,
  syncAllExceptionsAccess,
  syncAllExceptionsAccessSuccess,
  loadAllDADeliverablesTemplates,
  setAllDADeliverablesTemplates,
  createDADeliverableTemplateFromForm,
  createDADeliverableTemplateFromFormSuccess,
  loadSMEDisciplineConfigCodes,
  setSMEDisciplineConfigCodesSuccess,
  loadDADeliverableTemplate,
  setDADeliverableTemplateForm,
  updateDADeliverableTemplateForm,
  updateDADeliverableTemplateFormSuccess,
  deleteDADeliverableTemplate,
  deleteDADeliverableTemplateSuccess,
  loadAllBusinessUnits,
  setAllBusinessUnits,
  loadBusinessUnit,
  setBusinessUnitForm,
  createBusinessUnitFromForm,
  createBusinessUnitFromFormSuccess,
  deleteBusinessUnit,
  updateBusinessUnitForm,
  updateBusinessUnitFormSuccess,
  getAssignments,
  setAssignments,
  loadAllFocusAreaConfigs,
  setAllFocusAreaConfigs,
  setFocusAreaConfigForm,
  loadFocusAreaConfig,
  createFocusAreaConfigFromForm,
  createFocusAreaConfigFromFormSuccess,
  updateFocusAreaConfigForm,
  updateFocusAreaConfigFormSuccess,
  deleteFocusAreaConfigSuccess,
  deleteFocusAreaConfig,
} from './global-settings.actions';
import {
  selectBusinessUnitForm,
  selectDADeliverableTemplateForm,
  selectFocusAreaConfigForm,
  selectGlobalSettingsValueRecognitionForm,
  selectMandatoryCheckTemplateForm,
  selectPeerPoolDisciplineForm,
} from './global-settings.selectors';
import {
  sendReleaseEmail,
  sendReleaseEmailSuccess,
  setAllExceptionsAccess,
  setAllGlobalAdminsAccess,
} from 'src/app/roles/_shared-by-role/global-settings/store/global-settings.actions';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { combineLatest, of } from 'rxjs';
import { Constants } from 'src/app/shared/utils/constants';

@Injectable()
export class GlobalSettingsDAEngineerEffects {
  loadGlobalSettingsValueRecognition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadGlobalSettingsValueRecognition),
      startLoading(this.store),
      mergeMap(() =>
        this.globalSettingsService.getValueRecognitions().pipe(
          map((globalSettingsValueRecognition) =>
            setGlobalSettingsValueRecognition({
              globalSettingsValueRecognition,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  updateGlobalSettingsValueRecognition$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateGlobalSettingsValueRecognition),
        startLoading(this.store),
        withLatestFrom(
          this.store.select(selectGlobalSettingsValueRecognitionForm)
        ),
        mergeMap(([_, globalSettingsValueRecognitionForm]) => {
          const viewModelToSave = [
            {
              ...globalSettingsValueRecognitionForm.value.phase2,
              phaseNumber: 2,
            } as GlobalSettingsValueRecognitionViewModel,
            {
              ...globalSettingsValueRecognitionForm.value.phase3,
              phaseNumber: 3,
            } as GlobalSettingsValueRecognitionViewModel,
            {
              ...globalSettingsValueRecognitionForm.value.phase4,
              phaseNumber: 4,
            } as GlobalSettingsValueRecognitionViewModel,
          ];
          return this.globalSettingsService.updateValueRecognitions(
            viewModelToSave
          );
        }),
        tap(() =>
          this.toastMessagesService.displaySuccess(
            `Success: Global Value Recognition saved`
          )
        ),
        finishLoading(this.store)
      ),
    { dispatch: false }
  );

  loadAllMandatoryChecksTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllMandatoryChecksTemplates),
      startLoading(this.store),
      mergeMap(() =>
        this.mandatoryCheckTemplateService.getAll().pipe(
          map((mandatoryChecksTemplates) =>
            setAllMandatoryChecksTemplates({
              mandatoryChecksTemplates,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  loadMandatoryCheckTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMandatoryCheckTemplate),
      startLoading(this.store),
      mergeMap(({ mandatoryCheckTemplateId }) =>
        this.mandatoryCheckTemplateService
          .getById(mandatoryCheckTemplateId)
          .pipe(
            map((mandatoryCheckTemplate) =>
              setMandatoryCheckTemplateForm({
                mandatoryCheckTemplate,
              })
            )
          )
      ),
      finishLoading(this.store)
    )
  );

  createMandatoryCheckTemplateFromForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createMandatoryCheckTemplateFromForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectMandatoryCheckTemplateForm)),
      mergeMap(([_, mandatoryCheckTemplateForm]) => {
        const mandatoryCheckTemplateViewModel = {
          ...mandatoryCheckTemplateForm.value,
          relevantPhases: mandatoryCheckTemplateForm.value.relevantPhases.value,
        } as any;
        return this.mandatoryCheckTemplateService.create(
          mandatoryCheckTemplateViewModel
        );
      }),
      map((mandatoryCheckTemplate) =>
        createMandatoryCheckTemplateFromFormSuccess({ mandatoryCheckTemplate })
      ),
      finishLoading(this.store)
    )
  );

  createMandatoryCheckTemplateFromFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createMandatoryCheckTemplateFromFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: new mandatory check template created`
        );
        return loadAllMandatoryChecksTemplates();
      })
    )
  );

  deleteMandatoryCheckTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteMandatoryCheckTemplate),
      startLoading(this.store),
      mergeMap(({ mandatoryCheckTemplateId }) =>
        this.mandatoryCheckTemplateService.delete(mandatoryCheckTemplateId)
      ),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: Mandatory Check Template has been deleted.`
        );
        return loadAllMandatoryChecksTemplates();
      }),
      finishLoading(this.store)
    )
  );

  updateMandatoryCheckTemplateForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMandatoryCheckTemplateForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectMandatoryCheckTemplateForm)),
      mergeMap(([{ mandatoryCheckTemplateId }, mandatoryCheckTemplateForm]) =>
        this.mandatoryCheckTemplateService.update(mandatoryCheckTemplateId, {
          ...mandatoryCheckTemplateForm.value,
          id: mandatoryCheckTemplateId,
          relevantPhases: mandatoryCheckTemplateForm.value.relevantPhases.value,
        } as MandatoryCheckTemplateViewModel)
      ),
      map((mandatoryCheckTemplate) =>
        updateMandatoryCheckTemplateFormSuccess({ mandatoryCheckTemplate })
      ),
      finishLoading(this.store)
    )
  );

  updateMandatoryCheckTemplateFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMandatoryCheckTemplateFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: Mandatory Check Template has been updated.`
        );
        return loadAllMandatoryChecksTemplates();
      })
    )
  );

  loadAllPhases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllPhases),
      mergeMap(() =>
        this.projectPhaseTemplateService.getAll().pipe(
          map((phases) =>
            setAllPhases({
              allPhases: phases.map((p) => p.phaseId),
            })
          )
        )
      )
    )
  );

  syncAllGlobalAdminsAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncAllGlobalAdminsAccess),
      startLoading(this.store),
      mergeMap(({ globalAdminsIds }) => {
        const globalAdmins = globalAdminsIds.map(
          (oid) =>
            ({
              oid,
            } as GlobalAdminViewModel)
        );
        return this.globalAdminService
          .syncGlobalAdmins(globalAdmins)
          .pipe(
            map(() => syncAllGlobalAdminsAccessSuccess({ globalAdminsIds }))
          );
      }),
      finishLoading(this.store)
    )
  );

  syncAllGlobalAdminsAccessSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncAllGlobalAdminsAccessSuccess),
      map(({ globalAdminsIds }) => {
        this.toastMessagesService.displaySuccess(
          `Success: Global Admins have been updated.`
        );
        return setAllGlobalAdminsAccess({ globalAdminsIds });
      })
    )
  );

  loadAllPeerPoolDisciplines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllPeerPoolDisciplines),
      startLoading(this.store),
      mergeMap(() =>
        this.SMEDisciplineService.getAllSMEDisciplineConfigs().pipe(
          mergeMap((peerPoolDisciplines) => {
            const peerPoolDisciplinesWithManagerNames = peerPoolDisciplines.map(
              (discipline) => {
                const managerNames$ =
                  discipline.chapterManagers?.map((managerId) =>
                    this.userDataService.getUserName$(managerId)
                  ) ?? [];

                return combineLatest(
                  managerNames$.length > 0 ? managerNames$ : [of(null)]
                ).pipe(
                  map((managerNames) => ({
                    ...discipline,
                    chapterManagersNames: managerNames.join('; '),
                  })),
                  startWith(discipline)
                );
              }
            );

            return combineLatest(peerPoolDisciplinesWithManagerNames).pipe(
              debounceTime(300)
            );
          }),
          map((peerPoolDisciplinesWithManagerNames) =>
            setAllPeerPoolDisciplines({
              peerPoolDisciplines:
                peerPoolDisciplinesWithManagerNames as SMEDisciplineConfigViewModel[],
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  loadPeerPoolDiscipline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPeerPoolDiscipline),
      startLoading(this.store),
      mergeMap(({ peerPoolDisciplineId }) =>
        this.SMEDisciplineService.getSMEDisciplineConfigPeerPool(
          peerPoolDisciplineId
        ).pipe(
          map((peerPoolDiscipline) =>
            setPeerPoolDisciplineForm({
              peerPoolDiscipline,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  createPeerPoolDiscipline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPeerPoolDiscipline),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectPeerPoolDisciplineForm)),
      mergeMap(([_, peerPoolDisciplineForm]) =>
        this.SMEDisciplineService.createCustomSMEDisciplineConfig({
          ...peerPoolDisciplineForm.value,
          chapterManagers:
            peerPoolDisciplineForm.value.chapterManagers.value.filter((x) => x),
        } as any)
      ),
      mergeMap((peerPoolDiscipline) => [
        createPeerPoolDisciplineSuccess({ peerPoolDiscipline }),
      ]),
      finishLoading(this.store)
    )
  );

  createPeerPoolDisciplineSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPeerPoolDisciplineSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: new peer pool discipline created`
        );
        return loadAllPeerPoolDisciplines();
      })
    )
  );

  deletePeerPoolDiscipline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deletePeerPoolDiscipline),
      startLoading(this.store),
      mergeMap(({ peerPoolDisciplineId }) =>
        this.SMEDisciplineService.deleteCustomSMEDisciplineConfig(
          peerPoolDisciplineId
        )
      ),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: peer pool discipline has been deleted.`
        );
        return loadAllPeerPoolDisciplines();
      }),
      finishLoading(this.store)
    )
  );

  updatePeerPoolDiscipline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePeerPoolDiscipline),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectPeerPoolDisciplineForm)),
      mergeMap(([{ peerPoolDisciplineId }, peerPoolDisciplineForm]) =>
        this.SMEDisciplineService.updateCustomSMEDisciplineConfig(
          peerPoolDisciplineId,
          {
            ...peerPoolDisciplineForm.value,
            id: peerPoolDisciplineId,
            name: peerPoolDisciplineForm.value.name!,
            code: peerPoolDisciplineForm.value.code!,
            chapterManagers:
              peerPoolDisciplineForm.value.chapterManagers.value.filter(
                (x) => x
              ),
          } as unknown as SMEDisciplineConfigViewModel
        )
      ),
      map((peerPoolDiscipline) =>
        updatePeerPoolDisciplineSuccess({ peerPoolDiscipline })
      ),
      finishLoading(this.store)
    )
  );

  updatePeerPoolDisciplineSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePeerPoolDisciplineSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: peer pool discipline has been updated.`
        );
        return loadAllPeerPoolDisciplines();
      })
    )
  );

  importMandatoryChecks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(importMandatoryChecks),
      startLoading(this.store),
      mergeMap((importMandatoryChecks) =>
        this.mandatoryCheckService.import(
          importMandatoryChecks.file,
          importMandatoryChecks.onlyAdd,
          importMandatoryChecks.insertMissing,
          importMandatoryChecks.removeDeleted,
          importMandatoryChecks.updateExisting
        )
      ),
      map((mandatoryChecks) => {
        importMandatoryChecksSuccess({ mandatoryChecks });
        this.toastMessagesService.displaySuccess(
          `Success: Mandatory Check Templates have been imported.`
        );
        return loadAllMandatoryChecksTemplates();
      }),
      finishLoading(this.store)
    )
  );

  syncAllExceptionsAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncAllExceptionsAccess),
      startLoading(this.store),
      mergeMap(({ exceptionsIds }) => {
        const exceptions = exceptionsIds.map(
          (oid) =>
            ({
              oid,
            } as PeerPoolExceptionViewModel)
        );
        return this.exceptionService
          .syncExceptions(exceptions)
          .pipe(map(() => syncAllExceptionsAccessSuccess({ exceptionsIds })));
      }),
      finishLoading(this.store)
    )
  );

  syncAllExceptionsAccessSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncAllExceptionsAccessSuccess),
      map(({ exceptionsIds }) => {
        this.toastMessagesService.displaySuccess(
          `Success: Exceptions have been updated.`
        );
        return setAllExceptionsAccess({ exceptionsIds });
      })
    )
  );

  loadAllDADeliverablesTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllDADeliverablesTemplates),
      startLoading(this.store),
      mergeMap(() => {
        return this.daDeliverableTemplateService
          .getAllDADeliverableTemplates()
          .pipe(
            map((daDeliverablesTemplates) =>
              setAllDADeliverablesTemplates({
                daDeliverablesTemplates,
              })
            )
          );
      }),
      finishLoading(this.store)
    )
  );

  createDADeliverableTemplateFromForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createDADeliverableTemplateFromForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectDADeliverableTemplateForm)),
      mergeMap(([_, daDeliverableTemplateForm]) => {
        const selectedPhases =
          daDeliverableTemplateForm.value.phaseGuidelines.value || [];

        const phaseGuidelines = selectedPhases.map((phase) => ({
          projectPhaseTemplate: {
            phaseId: phase,
          },
          deliverableCategory: '',
          typicallyNOJVProvided: false,
          typicallyDASampled: false,
          daDeliverableTemplateId: Constants.GUID_DEFAULT_VALUE,
          daDeliverableTemplate: {
            DADeliverablePhaseGuidelines: [],
            origin: '',
          },
          workloadEstimates: [],
        }));

        const daDeliverableTemplateToCreate = {
          ...daDeliverableTemplateForm.value,
          DADeliverablePhaseGuidelines: phaseGuidelines,
        };

        return this.daDeliverableTemplateService.createDADeliverableTemplate(
          daDeliverableTemplateToCreate as any
        );
      }),
      map((daDeliverableTemplate) =>
        createDADeliverableTemplateFromFormSuccess({
          daDeliverableTemplate: daDeliverableTemplate,
        })
      ),
      finishLoading(this.store)
    )
  );

  createDADeliverableTemplateFromFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createDADeliverableTemplateFromFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: new DA deliverable template created`
        );
        return loadAllDADeliverablesTemplates();
      })
    )
  );

  createFocusAreaConfigFromForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createFocusAreaConfigFromForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectFocusAreaConfigForm)),
      mergeMap(([_, focusAreaConfigForm]) => {
        return this.riskAssessmentConfigService.createFocusAreaConfig(
          focusAreaConfigForm.value as FocusAreaConfigViewModel
        );
      }),
      map((focusAreaConfig) =>
        createFocusAreaConfigFromFormSuccess({
          focusAreaConfig,
        })
      ),
      finishLoading(this.store)
    )
  );

  createFocusAreaConfigFromFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createFocusAreaConfigFromFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: new focus area config created`
        );
        return loadAllFocusAreaConfigs();
      })
    )
  );

  loadAllFocusAreaConfigs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllFocusAreaConfigs),
      startLoading(this.store),
      mergeMap(() => {
        return this.riskAssessmentConfigService.getAllFocusAreaConfigs().pipe(
          map((focusAreaConfigs) =>
            setAllFocusAreaConfigs({
              focusAreaConfigs,
            })
          )
        );
      }),
      finishLoading(this.store)
    )
  );

  loadFocusAreaConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFocusAreaConfig),
      startLoading(this.store),
      mergeMap(({ focusAreaConfigId }) =>
        this.riskAssessmentConfigService
          .getFocusAreaConfigWithProjectPhaseTemplateDefinitions(
            focusAreaConfigId
          )
          .pipe(
            map((focusAreaConfig) =>
              setFocusAreaConfigForm({
                focusAreaConfig,
              })
            )
          )
      ),
      finishLoading(this.store)
    )
  );
  updateFocusAreaConfigForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFocusAreaConfigForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectFocusAreaConfigForm)),
      mergeMap(([_, focusAreaConfigForm]) => {
        return this.riskAssessmentConfigService.update(
          focusAreaConfigForm.value as FocusAreaConfigViewModel
        );
      }),
      map((focusAreaConfig) =>
        updateFocusAreaConfigFormSuccess({ focusAreaConfig })
      ),
      finishLoading(this.store)
    )
  );

  updateFocusAreaConfigFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFocusAreaConfigFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: focus area config has been updated.`
        );
        return loadAllFocusAreaConfigs();
      })
    )
  );

  deleteFocusAreaConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFocusAreaConfig),
      startLoading(this.store),
      mergeMap(({ focusAreaConfigId }) =>
        this.riskAssessmentConfigService.deleteFocusAreaConfig(
          focusAreaConfigId
        )
      ),
      map(() => deleteFocusAreaConfigSuccess()),
      finishLoading(this.store)
    )
  );

  deleteFocusAreaConfigSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFocusAreaConfigSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: focus area config has been deleted.`
        );
        return loadAllFocusAreaConfigs();
      })
    )
  );

  loadSMEDisciplineConfigCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSMEDisciplineConfigCodes),
      mergeMap(() =>
        this.SMEDisciplineService.getAllSMEDisciplineConfigs().pipe(
          map((disciplines) => {
            const codes = disciplines.map((discipline) => discipline.code!);
            return setSMEDisciplineConfigCodesSuccess({ codes });
          })
        )
      )
    )
  );

  loadDADeliverableTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDADeliverableTemplate),
      startLoading(this.store),
      mergeMap(({ daDeliverableTemplateId }) =>
        this.daDeliverableTemplateService.getById(daDeliverableTemplateId).pipe(
          map((daDeliverableTemplate) =>
            setDADeliverableTemplateForm({
              daDeliverableTemplate,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );
  updateDADeliverableTemplateForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDADeliverableTemplateForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectDADeliverableTemplateForm)),
      mergeMap(([{ daDeliverableTemplateId }, daDeliverableTemplateForm]) => {
        const selectedPhases =
          daDeliverableTemplateForm.value.phaseGuidelines.value || [];

        const phaseGuidelines = selectedPhases.map((phase) => ({
          projectPhaseTemplate: {
            phaseId: phase,
          },
          deliverableCategory: '',
          typicallyNOJVProvided: false,
          typicallyDASampled: false,
          daDeliverableTemplateId: daDeliverableTemplateId,
          daDeliverableTemplate: {
            DADeliverablePhaseGuidelines: [],
            origin: '',
          },
          workloadEstimates: [],
        }));

        const daDeliverableTemplateToEdit = {
          ...daDeliverableTemplateForm.value,
          DADeliverablePhaseGuidelines: phaseGuidelines,
          origin: '',
        };

        return this.daDeliverableTemplateService.update(
          daDeliverableTemplateToEdit as unknown as DADeliverableTemplateViewModel
        );
      }),
      map((daDeliverableTemplate) =>
        updateDADeliverableTemplateFormSuccess({ daDeliverableTemplate })
      ),
      finishLoading(this.store)
    )
  );

  updateDADeliverableTemplateFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDADeliverableTemplateFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: DA deliverable Template has been updated.`
        );
        return loadAllDADeliverablesTemplates();
      })
    )
  );

  deleteDADeliverableTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDADeliverableTemplate),
      startLoading(this.store),
      mergeMap(({ daDeliverableTemplateId }) =>
        this.daDeliverableTemplateService.delete(daDeliverableTemplateId)
      ),
      map(() => deleteDADeliverableTemplateSuccess()),
      finishLoading(this.store)
    )
  );

  deleteDADeliverableTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDADeliverableTemplateSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: DA deliverable template has deleted.`
        );
        return loadAllDADeliverablesTemplates();
      })
    )
  );

  sendReleaseEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendReleaseEmail),
      startLoading(this.store),
      mergeMap((data) =>
        this.globalSettingsService.sendReleaseEmail(data.role).pipe(
          tap(() => {
            this.toastMessagesService.displaySuccess(
              `Opening email client, email list copied to clipboard`
            );
          }),
          map((result) => sendReleaseEmailSuccess({ result }))
        )
      ),
      finishLoading(this.store)
    )
  );

  loadAllBusinessUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllBusinessUnits),
      startLoading(this.store),
      mergeMap(() =>
        this.businessUnitService.getAll().pipe(
          map((businessUnits) =>
            setAllBusinessUnits({
              businessUnits,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  loadBusinessUnit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBusinessUnit),
      startLoading(this.store),
      mergeMap(({ businessUnitId }) =>
        this.businessUnitService.getById(businessUnitId).pipe(
          map((businessUnit) =>
            setBusinessUnitForm({
              businessUnit,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  createBusinessUnitFromForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createBusinessUnitFromForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectBusinessUnitForm)),
      mergeMap(([_, businessUnitForm]) => {
        const businessUnitViewModel = {
          ...businessUnitForm.value,
        } as BusinessUnitViewModel;
        return this.businessUnitService.create(businessUnitViewModel);
      }),
      map((businessUnit) =>
        createBusinessUnitFromFormSuccess({ businessUnit })
      ),
      finishLoading(this.store)
    )
  );

  createBusinessUnitFromFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createBusinessUnitFromFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: new business unit created`
        );
        return loadAllBusinessUnits();
      })
    )
  );

  deleteBusinessUnit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteBusinessUnit),
      startLoading(this.store),
      mergeMap(({ businessUnitId }) =>
        this.businessUnitService.deleteBusinessUnit(businessUnitId)
      ),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: business unit has been deleted.`
        );
        return loadAllBusinessUnits();
      }),
      finishLoading(this.store)
    )
  );

  updateBusinessUnitForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBusinessUnitForm),
      startLoading(this.store),
      withLatestFrom(this.store.select(selectBusinessUnitForm)),
      mergeMap(([{ businessUnitId }, businessUnitForm]) =>
        this.businessUnitService.updateBusinessUnit(businessUnitId, {
          ...businessUnitForm.value,
          id: businessUnitId,
        } as BusinessUnitViewModel)
      ),
      map((businessUnit) => updateBusinessUnitFormSuccess({ businessUnit })),
      finishLoading(this.store)
    )
  );

  updateBusinessUnitFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBusinessUnitFormSuccess),
      map(() => {
        this.toastMessagesService.displaySuccess(
          `Success: business unit has been updated.`
        );
        return loadAllBusinessUnits();
      })
    )
  );

  loadAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAssignments),
      startLoading(this.store),
      mergeMap(() =>
        this.globalSettingsService.getAssignments().pipe(
          map((loadedAssignments) =>
            setAssignments({
              assignments: loadedAssignments,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private toastMessagesService: ToastMessagesService,
    private globalSettingsService: GlobalSettingsDAEngineerClient,
    private mandatoryCheckTemplateService: MandatoryCheckTemplateDAEngineerClient,
    private SMEDisciplineService: SMEDisciplineDAEngineerClient,
    private projectPhaseTemplateService: ProjectPhaseTemplateDAEngineerClient,
    private globalAdminService: GlobalAdminsClient,
    private exceptionService: ExceptionsClient,
    private userDataService: UserDataService,
    private mandatoryCheckService: MandatoryCheckTemplateDAEngineerClient,
    private daDeliverableTemplateService: DADeliverableTemplateDAEngineerClient,
    private businessUnitService: BusinessUnitDAEngineerClient,
    private riskAssessmentConfigService: RiskAssessmentConfigDAEngineerClient
  ) {}
}
